import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { View, Text, FlatList, TouchableOpacity, Modal, TextInput, Button, StyleSheet, Alert, Image, ActivityIndicator, RefreshControl } from 'react-native';
import { fetchAllCommunities, fetchUserCommunities, joinCommunity, createCommunity, refreshCommunityMemberCounts } from '../../services/communityService';
import { useAuth } from '../../Utils/AuthProvider';
import { useNavigation } from '@react-navigation/native';
import { launchImageLibrary } from 'react-native-image-picker';
import { PermissionManager } from '../../Utils/permissions';
import { uploadFile, getSupabaseFileUrl } from '../../services/imageService';
import { validateFileSize, showFileSizeError, showFileSizeInfo } from '../../Utils/fileValidation';
import { theme } from '../../constants/theme';
import Loading from '../../Components/Loading';
import ScreenWrapper from '../../Components/ScreenWrapper';
import Header from '../../Components/Header';
import defaultCommunityImg from '../../../assets/images/defaultuser.png';
import { Search, X } from '../../../assets/icons';

interface Community {
  id: string;
  name: string;
  description?: string;
  created_by: string;
  created_at: string;
  profile_image?: string;
  is_announcement_only?: boolean;
}

const Communities = () => {
  const { user } = useAuth();
  const navigation = useNavigation();
  const [communities, setCommunities] = useState<Community[]>([]);
  const [joinedCommunities, setJoinedCommunities] = useState<{ id: string; role: string }[]>([]);
  const [memberCounts, setMemberCounts] = useState<{ [communityId: string]: number }>({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState<{ [communityId: string]: boolean }>({});
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  
  // No pagination - load all communities at once

  const loadCommunities = async () => {
    setLoading(true);
    
    try {
      const [allRes, joinedRes, countsRes] = await Promise.all([
        fetchAllCommunities(), // No pagination parameters
        user ? fetchUserCommunities(user.id) : Promise.resolve({ success: true, data: [] as string[] }),
        refreshCommunityMemberCounts() // Use the proper function that tries database function first
      ]);
      
      let regularCommunities: Community[] = [];
      
      if (allRes.success) {
        // Filter out announcement-only communities since there's a dedicated Announcements page
        const announcementCommunities = allRes.data?.filter((community: any) => community.is_announcement_only) || [];
        regularCommunities = (allRes.data?.filter((community: any) => !community.is_announcement_only) || []) as Community[];
        
        setCommunities(regularCommunities as Community[]);
      } else {
        Alert.alert('Error', 'Failed to fetch communities');
      }
      
      if (joinedRes.success) {
        setJoinedCommunities(joinedRes.data as { id: string; role: string }[] || []);
      }
      
      if (countsRes.success && countsRes.data) {
        setMemberCounts(countsRes.data as { [communityId: string]: number } || {});
      } else {
        // Set default counts of 0 for all communities - avoid expensive fallback
        const defaultCounts: { [communityId: string]: number } = {};
        regularCommunities.forEach(community => {
          defaultCounts[community.id] = 0;
        });
        setMemberCounts(defaultCounts);
      }
    } catch (error) {
      console.error('Error loading communities:', error);
      Alert.alert('Error', 'Failed to load communities');
    } finally {
      setLoading(false);
    }
  };

  // No pagination needed - all communities loaded at once

  // Removed getIndividualMemberCounts function - no longer needed for performance

  useEffect(() => {
    loadCommunities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Removed debug logging for performance

  // Debounce search query to improve performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleJoin = async (communityId: string) => {
    if (!user) return Alert.alert('Error', 'You must be logged in to join a community');
    setJoining(j => ({ ...j, [communityId]: true }));
    const res = await joinCommunity(user.id, communityId);
    if (res.success) {
      // Update joined communities first
      setJoinedCommunities(jc => [...jc, { id: communityId, role: 'member' }]);
      
      // Optimize: just increment the count for this community instead of full refresh
      setMemberCounts(prev => ({
        ...prev,
        [communityId]: (prev[communityId] || 0) + 1
      }));
    } else {
      Alert.alert('Error', res.error?.message || 'Failed to join community');
    }
    setJoining(j => ({ ...j, [communityId]: false }));
  };

  const handlePickImage = async () => {
    // Request media permissions before launching picker
    const permissionResult = await PermissionManager.requestMediaPermissions();
    if (!permissionResult.granted) {
      Alert.alert('Permission Required', 'Gallery access is required to select an image.');
      return;
    }

    const result = await launchImageLibrary({ mediaType: 'photo', quality: 0.7 });
    if (result.assets && result.assets.length > 0) {
      const asset = result.assets[0];
      if (asset.uri) {
        // Validate file size before proceeding
        const sizeValidation = await validateFileSize(asset.uri);
        if (!sizeValidation.isValid) {
          showFileSizeError(sizeValidation.errorMessage || 'File size validation failed');
          return;
        }

        // Show file size info in console
        showFileSizeInfo(sizeValidation.size);

        setProfileImage(asset.uri);
      }
    }
  };


  const handleCreateCommunity = async () => {
    if (!user) return Alert.alert('Error', 'You must be logged in to create a community');
    if (!newName.trim()) return Alert.alert('Validation', 'Community name is required');
    setCreating(true);
    let imagePath = null;
    if (profileImage) {
      const uploadResult = await uploadFile('uploads', profileImage, true, null, 'community-profiles');
      imagePath = uploadResult && uploadResult.success ? uploadResult.data : null;
    }
    const res = await createCommunity({ name: newName.trim(), description: newDescription.trim(), created_by: user.id, profile_image: imagePath });
    if (res.success && res.data) {
      // Add creator as a member
      await joinCommunity(user.id, res.data.id);
      setShowModal(false);
      setNewName('');
      setNewDescription('');
      setProfileImage(null);
             // Reload communities to get fresh data
       loadCommunities();
    } else {
      Alert.alert('Error', res.error?.message || 'Failed to create community');
    }
    setCreating(false);
  };

  // Filter and sort communities based on search query and member count
  const filteredAndSortedCommunities = useMemo(() => {
    // Early return if no communities
    if (communities.length === 0) return [];
    
    let filtered = communities;
    
    // Apply search filter if there's a search query
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase().trim();
      // Optimized search with single pass and early returns
      filtered = communities.filter(community => {
        const name = community.name.toLowerCase();
        const description = community.description?.toLowerCase() || '';
        return name.includes(query) || description.includes(query);
      });
    }
    
    // Sort by member count in descending order - use stable sort for better performance
    return filtered.sort((a, b) => {
      const membersA = memberCounts[a.id] || 0;
      const membersB = memberCounts[b.id] || 0;
      return membersB - membersA; // Descending order (highest first)
    });
  }, [communities, memberCounts, debouncedSearchQuery]);

  // Search handler functions
  const handleSearchToggle = useCallback(() => {
    setShowSearch(!showSearch);
    if (showSearch) {
      setSearchQuery('');
    }
  }, [showSearch]);

  const handleSearchChange = useCallback((text: string) => {
    setSearchQuery(text);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  // Memoized community item component for better performance
  const CommunityItem = React.memo(({ item, memberCount, joinedCommunities, joining, user, onJoin, onPress }: { 
    item: Community; 
    memberCount: number;
    joinedCommunities: { id: string; role: string }[];
    joining: { [communityId: string]: boolean };
    user: any;
    onJoin: (communityId: string) => void;
    onPress: (communityId: string, profileImage?: string) => void;
  }) => {
    // Optimize: combine calculations to reduce memoization overhead
    const { joined, isCreator, userRole, isAdmin, isMember, isJoinable } = useMemo(() => {
      const joined = joinedCommunities.some(jc => jc.id === item.id);
      const isCreator = user?.id === item.created_by;
      const userRole = joinedCommunities.find(jc => jc.id === item.id)?.role;
      const isAdmin = userRole === 'admin';
      const isMember = joined;
      const isJoinable = !isCreator && !isAdmin && !isMember;
      
      return { joined, isCreator, userRole, isAdmin, isMember, isJoinable };
    }, [joinedCommunities, item.id, user?.id, item.created_by]);

    const handlePress = useCallback(() => {
      onPress(item.id, item.profile_image);
    }, [item.id, item.profile_image, onPress]);

    const handleJoinPress = useCallback(() => {
      onJoin(item.id);
    }, [item.id, onJoin]);

    // Removed excessive logging for performance
    
    return (
      <TouchableOpacity onPress={handlePress}>
        <View style={styles.communityItem}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
            <Image
              source={item.profile_image ? getSupabaseFileUrl(item.profile_image) as any : defaultCommunityImg}
              style={styles.communityAvatar}
              resizeMode="cover"
            />
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={styles.communityName}>{item.name}</Text>
              <Text style={styles.memberCount}>{memberCount} member{memberCount === 1 ? '' : 's'}</Text>
            </View>
          </View>
          <Text style={styles.communityDesc}>{item.description}</Text>
          {isJoinable ? (
            <TouchableOpacity
              style={[styles.joinButton, joined && styles.joinedButton]}
              disabled={joined || joining[item.id]}
              onPress={handleJoinPress}
            >
              <Text style={styles.joinButtonText}>{joined ? 'Joined' : (joining[item.id] ? 'Joining...' : 'Join')}</Text>
            </TouchableOpacity>
          ) : (
            <View style={[styles.roleButton, isCreator ? styles.creatorButton : styles.adminButton]}>
              <Text style={styles.roleButtonText}>
                {isCreator ? '👑 Creator' : isAdmin ? '⚡ Admin' : '✅ Member'}
              </Text>
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  }, (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    const joinedPrev = prevProps.joinedCommunities.some(jc => jc.id === prevProps.item.id);
    const joinedNext = nextProps.joinedCommunities.some(jc => jc.id === nextProps.item.id);
    
    // Check if all props are the same
    const itemSame = (
      prevProps.item.id === nextProps.item.id &&
      prevProps.item.name === nextProps.item.name &&
      prevProps.item.description === nextProps.item.description &&
      prevProps.item.profile_image === nextProps.item.profile_image &&
      prevProps.item.created_by === nextProps.item.created_by
    );
    
    const memberCountSame = prevProps.memberCount === nextProps.memberCount;
    const joinedSame = joinedPrev === joinedNext;
    const joiningSame = prevProps.joining[prevProps.item.id] === nextProps.joining[nextProps.item.id];
    const userSame = prevProps.user?.id === nextProps.user?.id;
    
    // Only re-render if something actually changed
    return itemSame && memberCountSame && joinedSame && joiningSame && userSame;
  });

  // Memoize the navigation handler to prevent recreation
  const handleCommunityPress = useCallback((communityId: string, profileImage?: string) => {
    (navigation as any).navigate('CommunityDetails', { communityId, profileImage });
  }, [navigation]);

  const renderItem = useCallback(({ item }: { item: Community }) => {
    const memberCount = memberCounts[item.id] || 0;
    
    return (
      <CommunityItem 
        item={item} 
        memberCount={memberCount}
        joinedCommunities={joinedCommunities}
        joining={joining}
        user={user}
        onJoin={handleJoin}
        onPress={handleCommunityPress}
      />
    );
  }, [memberCounts, joinedCommunities, joining, user, handleJoin, handleCommunityPress]);

  // Memoized keyExtractor for better performance
  const keyExtractor = useCallback((item: Community) => item.id, []);

  // Memoized getItemLayout for better performance
  const getItemLayout = useCallback((data: any, index: number) => {
    // Estimate item height: header (60px) + content (80px) + padding (32px) = ~172px
    const estimatedItemHeight = 172;
    return {
      length: estimatedItemHeight,
      offset: estimatedItemHeight * index,
      index,
    };
  }, []);

  // Memoized onRefresh handler
  const handleRefresh = useCallback(() => {
    loadCommunities();
  }, []);

  if (loading && communities.length === 0) {
    return (
      <ScreenWrapper bg="white">
        <View style={styles.centered}>
          <Loading />
          <Text style={styles.loadingText}>Loading communities...</Text>
        </View>
      </ScreenWrapper>
    );
  }

  return (
    <ScreenWrapper bg="white">
      <View style={styles.container}>
        <Header title="Communities" />
        <View style={styles.header}>
          <TouchableOpacity style={styles.searchButton} onPress={handleSearchToggle}>
            <Search size={24} color={theme.colors.primary} />
          </TouchableOpacity>
          <Button color={theme.colors.primaryDark} title="Create Community" onPress={() => setShowModal(true)} />
        </View>
        
        {/* Search Input */}
        {showSearch && (
          <View style={styles.searchContainer}>
            <View style={styles.searchInputContainer}>
              <Search size={20} color={theme.colors.textLight} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search communities..."
                placeholderTextColor={theme.colors.textLight}
                value={searchQuery}
                onChangeText={handleSearchChange}
                autoFocus={true}
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity onPress={handleClearSearch} style={styles.clearButton}>
                  <X size={20} color={theme.colors.textLight} />
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}
                    <FlatList
         data={filteredAndSortedCommunities}
         keyExtractor={keyExtractor}
         renderItem={renderItem}
         getItemLayout={getItemLayout}
         contentContainerStyle={styles.list}
         removeClippedSubviews={true}
         maxToRenderPerBatch={8}
         updateCellsBatchingPeriod={100}
         initialNumToRender={8}
         windowSize={8}
         maintainVisibleContentPosition={{
           minIndexForVisible: 0,
           autoscrollToTopThreshold: 10
         }}
         refreshControl={
           <RefreshControl
             refreshing={loading}
             onRefresh={handleRefresh}
             colors={[theme.colors.primary]}
             tintColor={theme.colors.primary}
           />
         }
        ListEmptyComponent={
          loading ? (
            <View style={styles.emptyLoading}>
              <ActivityIndicator size="large" color={theme.colors.primary} />
              <Text style={styles.emptyLoadingText}>Loading communities...</Text>
            </View>
          ) : debouncedSearchQuery.trim() ? (
            <View style={styles.emptySearch}>
              <Search size={48} color={theme.colors.textLight} />
              <Text style={styles.emptySearchText}>No communities found for "{debouncedSearchQuery}"</Text>
              <Text style={styles.emptySearchSubtext}>Try searching with different keywords</Text>
            </View>
          ) : (
            <Text style={styles.emptyText}>No communities found.</Text>
          )
        }
                 ListFooterComponent={
           filteredAndSortedCommunities.length > 0 ? (
             <View style={styles.listFooter}>
               <Text style={styles.listFooterText}>
                 {filteredAndSortedCommunities.length} community{filteredAndSortedCommunities.length === 1 ? '' : 's'} found
                 {debouncedSearchQuery.trim() && ` for "${debouncedSearchQuery}"`}
               </Text>
             </View>
           ) : null
         }
      />
      <Modal visible={showModal} animationType="slide" transparent>
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Create Community</Text>
            <TextInput
              placeholder="Community Name"
              value={newName}
              onChangeText={setNewName}
              style={styles.input}
              placeholderTextColor="#888"
            />
            <TextInput
              placeholder="Description"
              value={newDescription}
              onChangeText={setNewDescription}
              style={[styles.input, { height: 80 }]}
              multiline
              numberOfLines={3}
              placeholderTextColor="#888"
            />
            <TouchableOpacity style={styles.imagePicker} onPress={handlePickImage} disabled={uploading}>
              <Text style={styles.imagePickerText}>{profileImage ? 'Change' : 'Pick'} Community Image</Text>
            </TouchableOpacity>
            {profileImage && (
              <Image source={{ uri: profileImage }} style={styles.imagePreview} />
            )}
            <Button color={theme.colors.primary} title={creating || uploading ? 'Creating...' : 'Create'} onPress={handleCreateCommunity} disabled={creating || uploading} />
            <Button title="Cancel" onPress={() => setShowModal(false)} color="red" disabled={creating} />
          </View>
        </View>
      </Modal>
      </View>
    </ScreenWrapper>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16 },
  searchButton: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: theme.colors.primaryLight,
  },
  searchContainer: {
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: theme.colors.primaryLight,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.text,
    paddingVertical: 4,
  },
  clearButton: {
    padding: 4,
    marginLeft: 8,
  },
  list: { paddingHorizontal: 16 },
  communityItem: { backgroundColor: '#f2f2f2', padding: 16, borderRadius: 8, marginBottom: 12 },
  communityName: { fontSize: 18, fontWeight: 'bold', color: '#222' },
  memberCount: { fontSize: 14, color: theme.colors.primary, fontWeight: 'bold' },
  communityDesc: { fontSize: 14, color: '#555', marginBottom: 8 },
  joinButton: { backgroundColor: theme.colors.primary, padding: 8, borderRadius: 6, alignItems: 'center' },
  joinedButton: { backgroundColor: '#aaa' },
  joinButtonText: { color: '#fff', fontWeight: 'bold' },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  modalContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.3)' },
  modalContent: { backgroundColor: '#fff', padding: 24, borderRadius: 10, width: '80%' },
  modalTitle: { fontSize: 20, fontWeight: 'bold', marginBottom: 16 },
  input: { borderWidth: 1, borderColor: '#ccc', borderRadius: 6, padding: 8, marginBottom: 12, color: '#222' },
  imagePicker: {
    backgroundColor: theme.colors.primary,
    padding: 10,
    borderRadius: 6,
    alignItems: 'center',
    marginBottom: 10,
  },
  imagePickerText: {
    color: '#fff',
    fontWeight: 'bold',
  },
  imagePreview: {
    width: 80,
    height: 80,
    borderRadius: 40,
    alignSelf: 'center',
    marginBottom: 12,
  },
  
  communityAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#ddd',
  },
  roleButton: {
    backgroundColor: theme.colors.primaryLight,
    padding: 8,
    borderRadius: 6,
    alignItems: 'center',
    marginTop: 8,
  },
  roleButtonText: {
    color: theme.colors.primaryDark,
    fontWeight: 'bold',
  },
  creatorButton: {
    backgroundColor: theme.colors.success,
  },
  adminButton: {
    backgroundColor: theme.colors.warning,
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#555',
  },
  emptyLoading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 20,
  },
  emptyLoadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#555',
  },
  emptyText: {
    fontSize: 16,
    color: '#555',
    textAlign: 'center',
    paddingVertical: 20,
  },
  emptySearch: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptySearchText: {
    fontSize: 18,
    color: theme.colors.text,
    fontWeight: '600',
    marginTop: 16,
    textAlign: 'center',
  },
  emptySearchSubtext: {
    fontSize: 14,
    color: theme.colors.textLight,
    marginTop: 8,
    textAlign: 'center',
  },
  listFooter: {
    paddingVertical: 10,
    alignItems: 'center',
  },
  listFooterText: {
    fontSize: 14,
    color: '#888',
  },
});

export default Communities; 